import { COMPONENT_CLASS_PREFIX, ROOT_ELEMENT_KEY, DATA_PRESET_ATTRIBUTE, CSS_PRESET_PREFIX } from './constants'
import type { ElementToUpdate, ElementWithPreset, PolyfillData, PresetsPolyfillData, PresetPolyfillData } from './types'
import type { BuilderComponentsData } from '@wix/thunderbolt-symbols'
import { supportsContainerStyleQueries } from '@wix/thunderbolt-commons'

/**
 * Get CSS property name with preset prefix
 */
const getCSSPropertyName = (key: string): string => `${CSS_PRESET_PREFIX}-${key}`

/**
 * Get the current preset from CSS custom property of a given element
 * @param element - The element to check for preset
 * @param presetVariableKey - The specific preset variable key to look for
 * @returns The current preset name from CSS or empty string
 */
const getCurrentPresetFromElement = (element: Element, presetVariableKey: string): string => {
	const computedStyle = window.getComputedStyle(element)
	const specificPreset = computedStyle.getPropertyValue(getCSSPropertyName(presetVariableKey)).trim()

	// If specific preset is found, return it
	if (specificPreset) {
		return specificPreset
	}

	return ''
}

/**
 * Build selector for an element with component ID
 * @param elementKey - The element key (e.g., 'root', 'button')
 * @param selector - The CSS selector
 * @param componentId - The component ID
 * @returns The final CSS selector
 */
const buildSelector = (elementKey: string, selector: string, componentId: string): string => {
	return elementKey === ROOT_ELEMENT_KEY
		? `.${COMPONENT_CLASS_PREFIX}${componentId}`
		: `.${COMPONENT_CLASS_PREFIX}${componentId} ${selector}`
}

/**
 * Find DOM elements for a specific element configuration
 */
const findElementsForConfig = (
	elementConfig: ElementWithPreset,
	componentId: string
): Array<{ element: Element; elementKey: string }> => {
	const finalSelector = buildSelector(elementConfig.elementKey, elementConfig.selector, componentId)
	const results: Array<{ element: Element; elementKey: string }> = []

	const domElements = document.querySelectorAll(finalSelector)
	domElements.forEach((domElement) => {
		results.push({
			element: domElement,
			elementKey: elementConfig.elementKey,
		})
	})

	return results
}

/**
 * Extract preset data from found elements
 */
const extractPresetFromElements = (
	elements: Array<{ element: Element; elementKey: string }>,
	presetVariableKey: string
): Array<ElementToUpdate> => {
	return elements.map(({ element, elementKey }) => {
		const preset = getCurrentPresetFromElement(element, presetVariableKey)
		return {
			element,
			preset,
			elementKey,
		}
	})
}

/**
 * Process a single polyfill data entry
 */
const processPolyfillDataEntry = (polyfillData: PolyfillData): Array<ElementToUpdate> => {
	if (!polyfillData?.elementsWithPresets || !polyfillData?.componentId) {
		return []
	}

	const componentId = polyfillData.componentId
	const elementsToUpdate: Array<ElementToUpdate> = []

	polyfillData.elementsWithPresets.forEach((elementConfig) => {
		const foundElements = findElementsForConfig(elementConfig, componentId)
		const elementsWithPresets = extractPresetFromElements(foundElements, elementConfig.presetVariableKey)
		elementsToUpdate.push(...elementsWithPresets)
	})

	return elementsToUpdate
}

/**
 * Prepare all elements that need preset updates
 * @param presetsPolyfillData - The polyfill data to process
 * @returns Array of elements to update
 */
const prepareElementsToUpdate = (polyfillDataEntries: Array<[string, PolyfillData]>): Array<ElementToUpdate> => {
	const elementsToUpdate: Array<ElementToUpdate> = []

	polyfillDataEntries.forEach(([_builderType, polyfillData]) => {
		const processedElements = processPolyfillDataEntry(polyfillData)
		elementsToUpdate.push(...processedElements)
	})

	return elementsToUpdate
}

/**
 * Update all prepared elements with their preset attributes
 * @param elementsToUpdate - Array of elements to update
 */
const updateElementsWithPresets = (elementsToUpdate: Array<ElementToUpdate>): void => {
	elementsToUpdate.forEach(({ element, preset }) => {
		const hasPreset = preset && preset.trim()

		// Set the data-preset attribute if preset has a non-empty value, otherwise remove it
		if (hasPreset) {
			element.setAttribute(DATA_PRESET_ATTRIBUTE, preset)
		} else {
			element.removeAttribute(DATA_PRESET_ATTRIBUTE)
		}
	})
}

/**
 * Process presets polyfill data with direct updates
 * @param presetsPolyfillData - The polyfill data to process
 */
export const processPresetsPolyfill = (presetsPolyfillData: PresetsPolyfillData): void => {
	const polyfillDataEntries = Object.entries(presetsPolyfillData) as Array<[string, PolyfillData]>
	if (polyfillDataEntries.length === 0 || supportsContainerStyleQueries()) {
		return
	}

	try {
		// Step 1: Prepare all elements that need updates
		const elementsToUpdate = prepareElementsToUpdate(polyfillDataEntries)

		// Step 2: Update all elements (this will override existing values)
		updateElementsWithPresets(elementsToUpdate)
	} catch (error) {
		console.error('Presets Polyfill - Direct Update Error:', error)
	}
}

/**
 * Create polyfill data with componentId from builder components data
 * @param pagePolyfillData - The page polyfill data from config
 * @param builderComponentsData - The builder components data containing componentId mappings
 * @returns Processed polyfill data with componentId set for each builder type
 */
export const createPolyfillDataWithComponentId = (
	pagePolyfillData: Record<string, PresetPolyfillData>,
	builderComponentsData: BuilderComponentsData
): Record<string, PresetPolyfillData> => {
	if (!pagePolyfillData || !builderComponentsData) {
		return {}
	}

	const processedPresetsPolyfillData: Record<string, PresetPolyfillData> = {}

	const builderTypes = Object.keys(builderComponentsData.components)
	builderTypes.forEach((builderType) => {
		const data = builderComponentsData.components[builderType]
		if (pagePolyfillData[builderType]) {
			// Create clean elements without componentId
			const pageElements = pagePolyfillData[builderType].elementsWithPresets.map(
				({ elementKey, selector, presetVariableKey }) => ({
					elementKey,
					selector,
					presetVariableKey,
				})
			)

			// Set polyfill data with componentId at builder type level
			processedPresetsPolyfillData[builderType] = {
				componentId: data.componentId,
				elementsWithPresets: pageElements,
			}
		}
	})

	return processedPresetsPolyfillData
}

/**
 * Apply presets polyfill processing for a specific builder component
 * @param builderComponentId - The ID of the builder component to process
 * @param processedPresetsPolyfillData - The processed presets polyfill data with componentId
 */
export const applyPolyfillForComponent = (
	builderComponentId: string,
	processedPresetsPolyfillData: Record<string, PresetPolyfillData>
): void => {
	if (supportsContainerStyleQueries()) {
		return
	}

	// Find the builderType that matches this builderComponentId
	const matchingBuilderType = Object.keys(processedPresetsPolyfillData).find((builderType) => {
		const polyfillEntry = processedPresetsPolyfillData[builderType]
		return polyfillEntry?.componentId === builderComponentId
	})

	if (matchingBuilderType && processedPresetsPolyfillData[matchingBuilderType]) {
		// Use the polyfill system to set data-preset attributes
		const polyfillDataForComponent = {
			[matchingBuilderType]: processedPresetsPolyfillData[matchingBuilderType],
		}
		processPresetsPolyfill(polyfillDataForComponent)
	}
}

export const resolveComponentUrl = (
	isEditor: boolean,
	isSSR: boolean,
	clientUrl: string,
	editorComponentUrl?: string,
	serverUrl?: string
) => {
	if (isSSR) {
		return serverUrl || clientUrl
	} else if (isEditor) {
		return editorComponentUrl || clientUrl
	}
	return clientUrl
}
